/**
 * Runtime-generated textures — no asset files, low draw-call count.
 *  - Ball BODY: one texture per skin (rim + body + gloss). The FACE (eyes,
 *    mouth) is NOT baked in — it's separate vector objects that animate
 *    (see Player.ts).
 *  - Obstacles: a few fixed "size buckets" so each arcade body matches its
 *    texture exactly (scale stays 1 → correct collision).
 */
import Phaser from 'phaser';
import { PLAYER } from '../core';
import type { Skin } from './skins';

/** Texture key for a skin's ball body. */
export function ballTextureKey(skinId: string): string {
  return `ball-${skinId}`;
}

/** Generate the ball body texture for a skin once (skipped if it exists). */
export function ensureBallTexture(scene: Phaser.Scene, skin: Skin): void {
  const key = ballTextureKey(skin.id);
  if (scene.textures.exists(key)) return;

  const r = PLAYER.radius;
  const d = r * 2;
  const g = scene.add.graphics();
  // Rim (darker outer edge).
  g.fillStyle(skin.rim, 1);
  g.fillCircle(r, r, r);
  // Body.
  g.fillStyle(skin.body, 1);
  g.fillCircle(r, r, r - Math.max(2, r * 0.12));
  // Glossy highlight, upper-left.
  g.fillStyle(skin.highlight, 0.32);
  g.fillCircle(r * 0.66, r * 0.6, r * 0.4);
  g.generateTexture(key, d, d);
  g.destroy();
}

/** Fixed obstacle pixel sizes. Each gets its own 1:1 texture. */
export const OBSTACLE_BUCKETS = [24, 32, 40, 46] as const;

export function obstacleKey(index: number): string {
  return `obstacle-${index}`;
}

/** Map a desired (continuous) size to the nearest bucket texture. */
export function pickObstacleTexture(size: number): { key: string; px: number } {
  let bestIndex = 0;
  let bestDelta = Infinity;
  OBSTACLE_BUCKETS.forEach((px, i) => {
    const delta = Math.abs(px - size);
    if (delta < bestDelta) {
      bestDelta = delta;
      bestIndex = i;
    }
  });
  return { key: obstacleKey(bestIndex), px: OBSTACLE_BUCKETS[bestIndex] };
}

/** Create the obstacle textures once per scene if not already present. */
export function ensureObstacleTextures(scene: Phaser.Scene): void {
  OBSTACLE_BUCKETS.forEach((px, i) => {
    const key = obstacleKey(i);
    if (scene.textures.exists(key)) return;
    const g = scene.add.graphics();
    g.fillStyle(0xef476f, 1);
    g.fillRect(0, 0, px, px);
    g.fillStyle(0xffffff, 0.18);
    g.fillRect(0, 0, px, Math.max(2, px * 0.18));
    g.generateTexture(key, px, px);
    g.destroy();
  });
}

/* ------------------------------------------------------------------------- *
 * Hazard textures (the twelve patterns) — teal/yellow sci-fi look.
 *
 * The legacy Avoid! art is dark-steel walls, bright laser beams, glowing
 * missiles/bombs and a turret muzzle. The Phaser HazardPool tints/scales these
 * by hazard kind, so each texture is authored 1:1 at a unit size and stretched
 * to fit the live geometry; one texture per visual type keeps the draw-call
 * count down. Colours below are the shared palette; the pool applies a
 * telegraph tint/alpha during the preview window and the solid look once active.
 * ------------------------------------------------------------------------- */

/**
 * Shared hazard palette — recreated from the legacy Avoid! art (the `attack_*`
 * PNGs): TEAL machinery + GOLD trim, RED lasers/warnings, STEEL-blue bolted walls.
 * Numbers are 0xRRGGBB.
 */
export const HAZARD_COLORS = {
  /** Laser beam (legacy `attack_laser.png` is a RED lance with a white-hot core). */
  laserCore: 0xff2d3a,
  laserGlow: 0xff4b57,
  laserHot: 0xffffff,
  /** Steel-blue bolted wall plate (legacy `attack_wall_*.png`). */
  wall: 0x5a7088,
  wallDark: 0x3c4f63,
  wallHi: 0x88a0b6,
  wallBolt: 0x2b3a49,
  wallBoltHi: 0x7d94a8,
  /** Red warning edge line on the crushing face. */
  warn: 0xe23b3b,
  /** Teal/gold missile (legacy `attack_missile.png`): teal body, gold nose+fins, red bands. */
  missileBody: 0x2ec4b6,
  missileBodyHi: 0x6fe0d4,
  missileGold: 0xffd166,
  missileBand: 0xc0392b,
  /** Bomb ordnance body — near-black sphere with a cool steel rim. */
  bombBody: 0x0c1418,
  bombBodyHi: 0x39555e,
  bombRim: 0x2ec4b6,
  /** Bomb fuse: dark stub + a hot spark tip that glows during the telegraph. */
  bombFuse: 0x3a3128,
  bombSparkCore: 0xfff4c2,
  bombSparkGlow: 0xff8b3d,
  /** Explosion palette (teal → yellow → orange) — legacy, kept for other FX. */
  blastTeal: 0x2ec4b6,
  blastYellow: 0xffe066,
  blastOrange: 0xff8b3d,
  blastDeep: 0x9f3a04,
  /** FIRE explosion ramp (white-hot core → yellow → orange → deep red ember). */
  fireWhite: 0xfff6d8,
  fireYellow: 0xffd23f,
  fireOrange: 0xff7a18,
  fireRed: 0xe23b1a,
  fireEmber: 0x7a1e00,
  /** Turret (legacy `attack_laser_turret.png`): teal body, gold spine, red accents. */
  turretBody: 0x2ec4b6,
  turretBright: 0x35d6c8,
  turretBodyDark: 0x1c8f86,
  turretBodyDeep: 0x12655e,
  turretGold: 0xf2c84b,
  turretAccent: 0xd83a3a,
  /** Brighter machinery accents from the reference art: steel hex plate + cyan glow edge. */
  turretSteel: 0xd7e6ec,
  turretSteelDark: 0x8aa0aa,
  turretCyan: 0x4fe3f0,
  /** Lock-on reticle (legacy `attack_target.png`): gold ring, red crosshair + dot. */
  targetRing: 0xffb020,
  targetRingHot: 0xffd24a,
  targetCross: 0xff3535,
  targetDot: 0xff2323,
} as const;

/** Texture keys for the hazard visuals — one per kind/type. */
export const HAZARD_TEX = {
  /** Axis-aligned wall bar (RectHazard). Authored 64x64, stretched to w/h. */
  wall: 'hz-wall',
  /** Tileable armored crusher panel — TILED across a wall (crisp at any size). 128x128. */
  wallPanel: 'hz-wall-panel',
  /** Straight laser beam (SegmentHazard / RotatingSegmentHazard beams). 16x64 strip, rotated/stretched. */
  beam: 'hz-beam',
  /** Missile body (a CircleMover for the missile family). 48x48, pointed. */
  missile: 'hz-missile',
  /** Bomb body (a CircleMover for the bomb family). 48x48, round. */
  bomb: 'hz-bomb',
  /** Turret hub — manta/stingray style (drawn at a turret muzzle anchor). 64x64. */
  turret: 'hz-turret',
  /** Turret hub — compact barrel-cannon style (the second turret variant). 64x64. */
  turretCannon: 'hz-turret-cannon',
  /** Lock-on reticle (the turret's tracking/charge target marker). 64x64. */
  target: 'hz-target',
  /** Caro emitter CORE disc (the carousel hub centre). 64x64. */
  caroCore: 'hz-caro-core',
  /** Caro BARREL arm — one drawn per live beam, rotated to it. 32x72, points up. */
  caroBarrel: 'hz-caro-barrel',
  /** Soft round glow particle for the explosion burst. 32x32 radial gradient. */
  spark: 'hz-spark',
  /** Expanding shockwave ring for the explosion. 128x128 hollow ring. */
  shock: 'hz-shock',
} as const;

/**
 * Generate every hazard texture once per scene (idempotent — skips any that
 * already exist). Call from the scene's `create()` before the HazardPool spawns.
 */
export function ensureHazardTextures(scene: Phaser.Scene): void {
  ensureWallTexture(scene);
  ensureWallPanelTexture(scene);
  ensureBeamTexture(scene);
  ensureMissileTexture(scene);
  ensureBombTexture(scene);
  ensureTurretTexture(scene);
  ensureTurretCannonTexture(scene);
  ensureTargetTexture(scene);
  ensureCaroCoreTexture(scene);
  ensureCaroBarrelTexture(scene);
  ensureSparkTexture(scene);
  ensureShockTexture(scene);
}

/**
 * Wall plate (crusher block): a sleek, high-tech DARK slab with a glowing CYAN energy
 * frame on all four edges, a softer inner glow, brushed-metal sheen and glowing corner
 * bolts — a clean, menacing crusher body. The MENACE (the spiked crushing teeth) is
 * drawn separately on the inner face by {@link import('./WallSpikeFx').WallSpikeFx}, so
 * this body stays edge-symmetric and survives an extreme stretch in any orientation.
 * Authored 64×64 and stretched to the live `w`×`h`.
 */
function ensureWallTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.wall)) return;
  const w = 64;
  const h = 64;
  const g = scene.add.graphics();

  // Dark metallic body + a slightly lighter inset panel.
  g.fillStyle(0x0b1116, 1);
  g.fillRect(0, 0, w, h);
  g.fillStyle(0x16242b, 1);
  g.fillRect(4, 4, w - 8, h - 8);
  // Panel bevel: light top/left, dark bottom/right.
  g.fillStyle(0xffffff, 0.07);
  g.fillRect(4, 4, w - 8, 2);
  g.fillRect(4, 4, 2, h - 8);
  g.fillStyle(0x000000, 0.3);
  g.fillRect(4, h - 6, w - 8, 2);
  g.fillRect(w - 6, 4, 2, h - 8);
  // Faint brushed sheen (a couple of vertical streaks).
  g.fillStyle(0xffffff, 0.04);
  g.fillRect(16, 6, 5, h - 12);
  g.fillRect(40, 6, 3, h - 12);
  // Soft teal inner core glow.
  g.fillStyle(HAZARD_COLORS.turretBody, 0.06);
  g.fillRect(10, 10, w - 20, h - 20);

  // Glowing CYAN energy frame: soft outer band + a crisp bright inner line.
  g.fillStyle(HAZARD_COLORS.turretCyan, 0.16);
  g.fillRect(0, 0, w, 4);
  g.fillRect(0, h - 4, w, 4);
  g.fillRect(0, 0, 4, h);
  g.fillRect(w - 4, 0, 4, h);
  g.fillStyle(HAZARD_COLORS.turretCyan, 0.95);
  g.fillRect(0, 0, w, 1.5);
  g.fillRect(0, h - 1.5, w, 1.5);
  g.fillRect(0, 0, 1.5, h);
  g.fillRect(w - 1.5, 0, 1.5, h);

  // Glowing corner bolts.
  for (const [rx, ry] of [
    [11, 11],
    [w - 11, 11],
    [11, h - 11],
    [w - 11, h - 11],
  ]) {
    g.fillStyle(0x06090c, 1);
    g.fillCircle(rx, ry, 3);
    g.fillStyle(HAZARD_COLORS.turretCyan, 0.8);
    g.fillCircle(rx, ry, 1.4);
  }

  g.generateTexture(HAZARD_TEX.wall, w, h);
  g.destroy();
}

/**
 * Tileable armored crusher PANEL — TILED (not stretched) across a wall by
 * {@link import('./WallBodyFx').WallBodyFx}, so the crusher reads as crisp, high-detail
 * plated armor at ANY wall size instead of one low-res box stretched out. 128×128,
 * seamlessly tileable: corner bolts sit on the panel corners (quarter per tile → a full
 * bolt forms where four panels meet), and a dark seam gutter rims each plate.
 */
function ensureWallPanelTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.wallPanel)) return;
  const s = 128;
  const c = s / 2;
  const g = scene.add.graphics();

  // Dark seam/gutter base (shows as recessed grooves between plates when tiled).
  g.fillStyle(0x070d12, 1);
  g.fillRect(0, 0, s, s);
  // Raised armor plate (inset, leaving the gutter seam).
  g.fillStyle(0x15252d, 1);
  g.fillRect(5, 5, s - 10, s - 10);
  // Plate bevel: light top/left, dark bottom/right.
  g.fillStyle(0xffffff, 0.06);
  g.fillRect(5, 5, s - 10, 3);
  g.fillRect(5, 5, 3, s - 10);
  g.fillStyle(0x000000, 0.34);
  g.fillRect(5, s - 8, s - 10, 3);
  g.fillRect(s - 8, 5, 3, s - 10);
  // Brushed-metal streaks (subtle verticals).
  g.fillStyle(0xffffff, 0.03);
  for (let x = 16; x < s - 8; x += 20) g.fillRect(x, 8, 3, s - 16);
  // Recessed inner sub-panel with its own bevel.
  g.fillStyle(0x0c1a20, 1);
  g.fillRect(24, 24, s - 48, s - 48);
  g.fillStyle(0x1e323b, 1);
  g.fillRect(27, 27, s - 54, s - 54);
  g.fillStyle(0xffffff, 0.05);
  g.fillRect(27, 27, s - 54, 2);
  g.fillStyle(0x000000, 0.28);
  g.fillRect(27, s - 29, s - 54, 2);
  // Centre teal energy accent (a soft glow + a bright diamond + hot core).
  g.fillStyle(HAZARD_COLORS.turretCyan, 0.1);
  g.fillCircle(c, c, 18);
  g.fillStyle(HAZARD_COLORS.turretCyan, 0.55);
  g.fillTriangle(c, c - 11, c + 11, c, c, c + 11);
  g.fillTriangle(c, c - 11, c - 11, c, c, c + 11);
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillCircle(c, c, 3.5);
  g.fillStyle(0xffffff, 0.5);
  g.fillCircle(c - 1, c - 1, 1.4);
  // Corner bolts (quarter per tile → a full glowing bolt at every panel junction).
  for (const [bx, by] of [
    [0, 0],
    [s, 0],
    [0, s],
    [s, s],
  ]) {
    g.fillStyle(0x05090c, 1);
    g.fillCircle(bx, by, 9);
    g.fillStyle(0x33454e, 1);
    g.fillCircle(bx, by, 5.5);
    g.fillStyle(HAZARD_COLORS.turretCyan, 0.75);
    g.fillCircle(bx, by, 2);
  }

  g.generateTexture(HAZARD_TEX.wallPanel, s, s);
  g.destroy();
}

/**
 * Laser beam: the legacy `attack_laser.png` RED lance — a soft red glow around a
 * saturated red core with a white-hot centre line. A 16×64 vertical strip so it
 * stretches cleanly to any length and rotates to any angle (turret beam, laser
 * walls, and the caro all draw from it).
 */
function ensureBeamTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.beam)) return;
  const w = 16;
  const h = 64;
  const g = scene.add.graphics();
  // Soft red outer glow (feathered in two bands).
  g.fillStyle(HAZARD_COLORS.laserGlow, 0.28);
  g.fillRect(0, 0, w, h);
  g.fillStyle(HAZARD_COLORS.laserGlow, 0.55);
  g.fillRect(3, 0, w - 6, h);
  // Saturated red core.
  const core = 6;
  g.fillStyle(HAZARD_COLORS.laserCore, 1);
  g.fillRect((w - core) / 2, 0, core, h);
  // White-hot centre line.
  g.fillStyle(HAZARD_COLORS.laserHot, 0.92);
  g.fillRect((w - 2) / 2, 0, 2, h);
  g.generateTexture(HAZARD_TEX.beam, w, h);
  g.destroy();
}

/**
 * Missile: recreated from the owner reference — a slim TEAL rocket with a sharp
 * teal/cyan nose, a GOLD collar, GOLD swept mid-fins and large GOLD flared tail
 * fins, three RED band markings on the lower body and a cyan highlight down the
 * body. Authored 64×64 (a touch larger than the old 48 for the longer nose + fins),
 * pointing UP (-y); the director rotates it to lead its travel direction.
 */
function ensureMissileTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.missile)) return;
  const s = 64;
  const c = s / 2;
  const g = scene.add.graphics();

  // Large gold flared tail fins (swept out at the base).
  g.fillStyle(HAZARD_COLORS.missileGold, 1);
  g.fillTriangle(c - 16, s - 2, c - 4, s - 20, c - 4, s - 2);
  g.fillTriangle(c + 16, s - 2, c + 4, s - 20, c + 4, s - 2);
  // Gold swept mid-fins (smaller, midway down, raked back).
  g.fillTriangle(c - 13, 40, c - 5, 30, c - 5, 42);
  g.fillTriangle(c + 13, 40, c + 5, 30, c + 5, 42);

  // Teal body capsule.
  g.fillStyle(HAZARD_COLORS.missileBody, 1);
  g.fillRoundedRect(c - 7, 14, 14, s - 26, 5);
  // Cyan/teal highlight down the left of the body.
  g.fillStyle(HAZARD_COLORS.missileBodyHi, 0.65);
  g.fillRect(c - 5, 16, 2.4, s - 32);
  // Soft right-side shade.
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 0.4);
  g.fillRect(c + 3, 16, 2.4, s - 32);

  // Sharp teal nose cone with a brighter cyan leading edge.
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillTriangle(c - 7, 16, c + 7, 16, c, 0);
  g.fillStyle(HAZARD_COLORS.missileBody, 1);
  g.fillTriangle(c - 5, 16, c + 5, 16, c, 3);
  g.fillStyle(HAZARD_COLORS.turretCyan, 0.9);
  g.fillTriangle(c - 2, 14, c + 2, 14, c, 4);
  // Gold collar where the nose meets the body.
  g.fillStyle(HAZARD_COLORS.missileGold, 1);
  g.fillRect(c - 7, 14, 14, 3);

  // Three red band markings on the lower body.
  g.fillStyle(HAZARD_COLORS.missileBand, 1);
  g.fillRect(c - 7, 30, 14, 2);
  g.fillRect(c - 7, 34, 14, 2);
  g.fillRect(c - 7, 38, 14, 2);

  g.generateTexture(HAZARD_TEX.missile, s, s);
  g.destroy();
}

/**
 * Bomb: a proper piece of ordnance — a near-black sphere with a cool steel rim
 * and a soft top-left rim-light, a short fuse stub on top, and a hot glowing
 * spark at the fuse tip. Authored at 64×64 (a touch larger than the old 48 so
 * the fuse + glow have room) and rendered centred on the bomb's body; the lit
 * fuse reads as a live threat and the director pulses the whole body during the
 * telegraph.
 */
function ensureBombTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.bomb)) return;
  const s = 64;
  const c = s / 2;
  // The spherical casing sits in the lower portion so the fuse fits up top.
  const cy = c + 4;
  const r = 22;
  const g = scene.add.graphics();

  // Soft teal aura behind the casing (telegraph glow base; the director boosts
  // alpha by pulsing the sprite, so keep this subtle).
  g.fillStyle(HAZARD_COLORS.bombRim, 0.18);
  g.fillCircle(c, cy, r + 5);

  // Steel rim ring.
  g.fillStyle(HAZARD_COLORS.bombRim, 0.9);
  g.fillCircle(c, cy, r);
  // Dark casing.
  g.fillStyle(HAZARD_COLORS.bombBody, 1);
  g.fillCircle(c, cy, r - 3);
  // Concentric shading rings (lighter toward the upper-left light source).
  g.fillStyle(HAZARD_COLORS.bombBodyHi, 0.35);
  g.fillCircle(c - r * 0.28, cy - r * 0.28, r * 0.62);
  g.fillStyle(HAZARD_COLORS.bombBody, 1);
  g.fillCircle(c - r * 0.18, cy - r * 0.18, r * 0.5);
  // Specular gloss hotspot (upper-left).
  g.fillStyle(0xffffff, 0.5);
  g.fillCircle(c - r * 0.42, cy - r * 0.42, r * 0.16);
  g.fillStyle(0xffffff, 0.2);
  g.fillCircle(c - r * 0.42, cy - r * 0.42, r * 0.28);

  // Fuse cap (a small dark cylinder on top of the casing).
  g.fillStyle(HAZARD_COLORS.bombBody, 1);
  g.fillRect(c - 5, cy - r - 6, 10, 8);
  g.fillStyle(HAZARD_COLORS.bombRim, 0.8);
  g.fillRect(c - 5, cy - r - 6, 10, 2);

  // Fuse cord (a short stub leaning to the upper-right).
  g.lineStyle(3, HAZARD_COLORS.bombFuse, 1);
  g.beginPath();
  g.moveTo(c, cy - r - 6);
  g.lineTo(c + 6, cy - r - 14);
  g.strokePath();

  // Fuse spark glow → core (hot, sits at the tip; this is the "lit fuse").
  const fx = c + 7;
  const fy = cy - r - 16;
  g.fillStyle(HAZARD_COLORS.bombSparkGlow, 0.45);
  g.fillCircle(fx, fy, 7);
  g.fillStyle(HAZARD_COLORS.blastYellow, 0.8);
  g.fillCircle(fx, fy, 4);
  g.fillStyle(HAZARD_COLORS.bombSparkCore, 1);
  g.fillCircle(fx, fy, 2);

  g.generateTexture(HAZARD_TEX.bomb, s, s);
  g.destroy();
}

/**
 * A soft round glow used as the explosion burst particle. A radial falloff
 * (bright white-hot centre fading to transparent) drawn as stacked translucent
 * discs, so ADD-blended copies pile into bright fiery cores. 32×32.
 */
function ensureSparkTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.spark)) return;
  const s = 32;
  const c = s / 2;
  const g = scene.add.graphics();
  // Build a fake radial gradient from soft outer to hot centre (white tinted at
  // runtime by the emitter's color ramp; keep the texture neutral/white).
  const stops = 8;
  for (let i = stops; i >= 1; i--) {
    const t = i / stops; // 1 = outer, → 0 = centre
    const radius = (c - 1) * t;
    const alpha = 0.16 * (1 - t) + 0.04; // brighter toward the centre
    g.fillStyle(0xffffff, alpha);
    g.fillCircle(c, c, radius);
  }
  // Crisp hot core.
  g.fillStyle(0xffffff, 1);
  g.fillCircle(c, c, 2.5);
  g.generateTexture(HAZARD_TEX.spark, s, s);
  g.destroy();
}

/**
 * The shockwave ring: a hollow white ring on a transparent field, authored at
 * 128×128 so it stays crisp when the explosion scales it up. Tinted teal/white
 * and ADD-blended at runtime, then scaled out + faded by a tween.
 */
function ensureShockTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.shock)) return;
  const s = 128;
  const c = s / 2;
  const g = scene.add.graphics();
  // Outer feathered band, then a bright crisp ring inside it.
  g.lineStyle(10, 0xffffff, 0.25);
  g.strokeCircle(c, c, c - 14);
  g.lineStyle(6, 0xffffff, 0.55);
  g.strokeCircle(c, c, c - 12);
  g.lineStyle(3, 0xffffff, 1);
  g.strokeCircle(c, c, c - 11);
  g.generateTexture(HAZARD_TEX.shock, s, s);
  g.destroy();
}

/**
 * Turret (manta style): recreated from the owner reference (img 6) — a TEAL
 * manta/stingray with broad wings, a GOLD central spine with chevron notches, RED
 * accents along the wing edges, a domed teal core and a barrel pointing UP (-y).
 * Authored 64×64 and drawn at a turret's muzzle anchor, rotated so the barrel aims
 * along the beam.
 */
function ensureTurretTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.turret)) return;
  const s = 64;
  const c = s / 2;
  const by = c + 8; // body centre sits below the barrel
  const g = scene.add.graphics();

  // Barrel (teal, gold-tipped) pointing up.
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillRect(c - 7, 0, 14, c - 1);
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillRect(c - 4, 0, 8, c - 1);
  g.fillStyle(HAZARD_COLORS.turretGold, 0.95);
  g.fillRect(c - 3, 0, 6, 6);

  // Manta wings: deep-teal underplate, brighter teal over, teal-bright top sheen.
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillTriangle(4, by + 18, s - 4, by + 18, c, by - 22);
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillTriangle(11, by + 13, s - 11, by + 13, c, by - 14);
  g.fillStyle(HAZARD_COLORS.turretBright, 0.6);
  g.fillTriangle(16, by + 2, s - 16, by + 2, c, by - 12);

  // Red accents sweeping up each wing edge.
  g.lineStyle(3, HAZARD_COLORS.turretAccent, 0.95);
  g.beginPath();
  g.moveTo(12, by + 12);
  g.lineTo(26, by - 9);
  g.strokePath();
  g.beginPath();
  g.moveTo(s - 12, by + 12);
  g.lineTo(s - 26, by - 9);
  g.strokePath();

  // Rounded belly + dark inner core.
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillCircle(c, by, 15);
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillCircle(c, by, 9);

  // Gold central spine with chevron notches climbing toward the barrel.
  g.fillStyle(HAZARD_COLORS.turretGold, 1);
  g.fillRect(c - 2, by - 20, 4, 30);
  g.fillStyle(HAZARD_COLORS.turretGold, 0.95);
  for (let i = 0; i < 4; i++) {
    const yy = by - 14 + i * 7;
    g.fillTriangle(c - 6, yy + 3, c + 6, yy + 3, c, yy - 3);
  }

  // Specular gloss (upper-left).
  g.fillStyle(0xffffff, 0.2);
  g.fillCircle(c - 6, by - 3, 4);

  g.generateTexture(HAZARD_TEX.turret, s, s);
  g.destroy();
}

/**
 * Turret (cannon style): recreated from the owner reference (img 5) — a compact TEAL
 * HEX-plated turret with a single barrel pointing UP (-y), a domed core with a RED
 * vertical stripe, RED diagonal accents at the top corners and a small GOLD nub.
 * The second turret variant; authored 64×64, drawn + rotated like the manta.
 */
function ensureTurretCannonTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.turretCannon)) return;
  const s = 64;
  const c = s / 2;
  const by = c + 9; // hex plate centre sits below the barrel
  const g = scene.add.graphics();

  // Barrel (teal, gold-tipped) pointing up.
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillRect(c - 8, 0, 16, by - 8);
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillRect(c - 5, 0, 10, by - 8);
  g.fillStyle(HAZARD_COLORS.turretBright, 0.6);
  g.fillRect(c - 4, 1, 2, by - 12);
  g.fillStyle(HAZARD_COLORS.turretGold, 0.95);
  g.fillRect(c - 4, 0, 8, 6);

  // Teal hex plate (pointy-top), deep-teal bevel under a teal face.
  const R = 25;
  const hex: Phaser.Math.Vector2[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (-90 + i * 60);
    hex.push(new Phaser.Math.Vector2(c + Math.cos(a) * R, by + Math.sin(a) * R));
  }
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillPoints(hex, true);
  const hexIn = hex.map((p) => new Phaser.Math.Vector2(c + (p.x - c) * 0.82, by + (p.y - by) * 0.82));
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillPoints(hexIn, true);
  g.fillStyle(HAZARD_COLORS.turretBright, 0.4); // upper sheen
  g.fillTriangle(c - R * 0.7, by - 4, c + R * 0.7, by - 4, c, by - R + 2);

  // Red diagonal accents at the top corners.
  g.lineStyle(3, HAZARD_COLORS.turretAccent, 0.95);
  g.beginPath();
  g.moveTo(c - 16, by - 8);
  g.lineTo(c - 8, by - 17);
  g.strokePath();
  g.beginPath();
  g.moveTo(c + 16, by - 8);
  g.lineTo(c + 8, by - 17);
  g.strokePath();

  // Domed core with a red vertical stripe.
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillCircle(c, by, 13);
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillCircle(c, by, 10);
  g.fillStyle(HAZARD_COLORS.turretAccent, 1);
  g.fillRect(c - 2.5, by - 11, 5, 20);
  // Small gold nub at the base of the dome + a gloss.
  g.fillStyle(HAZARD_COLORS.turretGold, 1);
  g.fillCircle(c, by + 9, 2.6);
  g.fillStyle(0xffffff, 0.22);
  g.fillCircle(c - 4, by - 4, 3);

  g.generateTexture(HAZARD_TEX.turretCannon, s, s);
  g.destroy();
}

/**
 * Lock-on reticle: the legacy `attack_target.png` — a GOLD ring with RED crosshair
 * spokes (N/S/E/W) and a RED centre dot. The turret FX places this on the player
 * while TRACKING, then freezes it on the locked aim point during the CHARGE so the
 * player can read exactly where the beam will fire. Authored 64×64.
 */
function ensureTargetTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.target)) return;
  const s = 64;
  const c = s / 2;
  const ring = 21;
  const g = scene.add.graphics();

  // Gold ring (a warm outer band + a hotter inner line).
  g.lineStyle(5, HAZARD_COLORS.targetRing, 0.95);
  g.strokeCircle(c, c, ring);
  g.lineStyle(2, HAZARD_COLORS.targetRingHot, 0.9);
  g.strokeCircle(c, c, ring - 1);

  // Red crosshair spokes reaching from the rim inward (gap before the centre dot).
  g.fillStyle(HAZARD_COLORS.targetCross, 1);
  g.fillRect(c - 2, 2, 4, 15); // N
  g.fillRect(c - 2, s - 17, 4, 15); // S
  g.fillRect(2, c - 2, 15, 4); // W
  g.fillRect(s - 17, c - 2, 15, 4); // E

  // Red centre dot with a tiny gloss.
  g.fillStyle(HAZARD_COLORS.targetDot, 1);
  g.fillCircle(c, c, 4.5);
  g.fillStyle(0xffffff, 0.45);
  g.fillCircle(c - 1.2, c - 1.2, 1.6);

  g.generateTexture(HAZARD_TEX.target, s, s);
  g.destroy();
}

/**
 * Caro CORE disc — the carousel hub centre the barrels mount on. A glowing-cyan steel
 * disc with a dark teal recessed core, a red accent ring and a gold centre. Authored
 * 64×64; CaroFx draws ONE per carousel at the centre, over the barrel bases. The
 * barrels ({@link ensureCaroBarrelTexture}) are drawn one-per-beam around it, so the
 * emitter has exactly as many arms as live beams (1/2/3/4) and they always align.
 */
function ensureCaroCoreTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.caroCore)) return;
  const s = 64;
  const c = s / 2;
  const g = scene.add.graphics();
  g.lineStyle(8, HAZARD_COLORS.turretCyan, 0.25); // soft outer cyan glow
  g.strokeCircle(c, c, 26);
  g.fillStyle(HAZARD_COLORS.turretSteel, 1); // steel disc
  g.fillCircle(c, c, 25);
  g.fillStyle(HAZARD_COLORS.turretSteelDark, 0.4);
  g.fillCircle(c, c, 22);
  g.lineStyle(2.5, HAZARD_COLORS.turretCyan, 0.9); // crisp inner cyan edge
  g.strokeCircle(c, c, 24);
  g.lineStyle(2, HAZARD_COLORS.turretAccent, 0.55); // red accent ring
  g.strokeCircle(c, c, 19);
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1); // dark teal recessed core
  g.fillCircle(c, c, 16);
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillCircle(c, c, 12);
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillCircle(c, c, 7);
  g.fillStyle(HAZARD_COLORS.turretGold, 1);
  g.fillCircle(c, c, 4);
  g.fillStyle(HAZARD_COLORS.bombSparkCore, 0.9);
  g.fillCircle(c, c, 1.8);
  g.fillStyle(0xffffff, 0.2); // gloss
  g.fillCircle(c - 6, c - 6, 4);
  g.generateTexture(HAZARD_TEX.caroCore, s, s);
  g.destroy();
}

/**
 * Caro BARREL arm — one teal/red cannon barrel matching the reference cross arms.
 * Authored 32×72 pointing UP (tip at the top, base at the bottom); CaroFx pivots it
 * at the base (the hub centre) and rotates it to a beam's heading, drawing one per
 * live beam — so for 1/2/3/4 beams the emitter shows 1/2/3/4 barrels and the beams
 * always exit a barrel (no more "weird Y" when 3 beams don't fit a fixed 4-cross).
 */
function ensureCaroBarrelTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HAZARD_TEX.caroBarrel)) return;
  const w = 32;
  const h = 72;
  const c = w / 2;
  const g = scene.add.graphics();
  // Dark teal casing + teal cylinder body.
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillRoundedRect(c - 15, 2, 30, 70, 8);
  g.fillStyle(HAZARD_COLORS.turretBody, 1);
  g.fillRoundedRect(c - 12, 4, 24, 66, 6);
  // Cylinder sheen (bright, left) + far-edge shade (dark, right).
  g.fillStyle(HAZARD_COLORS.turretBright, 0.7);
  g.fillRect(c - 9, 7, 3, 58);
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 0.5);
  g.fillRect(c + 7, 7, 2, 58);
  // Bold red racing stripe down the centre.
  g.fillStyle(HAZARD_COLORS.turretAccent, 1);
  g.fillRect(c - 4, 8, 8, 58);
  // Gold muzzle band near the tip, then a dark teal cap at the very tip.
  g.fillStyle(HAZARD_COLORS.turretGold, 1);
  g.fillRect(c - 12, 8, 24, 5);
  g.fillStyle(HAZARD_COLORS.turretBodyDeep, 1);
  g.fillRect(c - 12, 2, 24, 5);
  g.generateTexture(HAZARD_TEX.caroBarrel, w, h);
  g.destroy();
}
